import {
  type FandomPerformanceMarkTrackData,
  LoadTimeEvent,
  type LoadTimeTrackData,
  PerformanceMarkName,
  type TrackDataUnion,
  type TrackEvent,
} from "common-types";
import { EligibleEventsTracker } from "../Tracker";
import { sendTimeMeter } from "./sender/sendTimeMeter";
import {
  sendPerformanceDuration,
  sendPerformanceMark,
} from "./sender/sendPerformanceData";
import { isLoadTimeEvent } from "utils";
import { getPrometheusAlias } from "./utils/alias";
import { PrometheusTrackerConfig } from "./PrometheusTrackerConfig";

export class PrometheusTracker extends EligibleEventsTracker {
  protected readonly eligibleTrackEvents: TrackEvent[] = [
    LoadTimeEvent.AE_INIT,
    LoadTimeEvent.SLOT_REQUESTED,
    LoadTimeEvent.SLOT_RENDERED,
  ];
  private readonly eligiblePerformanceMarks = PrometheusTrackerConfig;
  private readonly performanceObserver;

  /**
   * Create PerformanceObserver which finds eligible PerformanceMarks and submits them as
   * AdEngine monitoring calls for Prometheus tracking. After a set timeout, the PerformanceObserver
   * will stop listening for new PerformanceMarks.
   */
  constructor() {
    super();
    this.performanceObserver = this.createPerformanceObserver();
    this.startPerformanceObserver(this.performanceObserver);
  }

  private createPerformanceObserver() {
    return new window.PerformanceObserver(
      (
        entries: PerformanceObserverEntryList,
        observer: PerformanceObserver,
      ): void => {
        this.trackPerformanceMarks(entries);
        this.disconnectPerformanceObserver(observer);
      },
    );
  }

  private startPerformanceObserver(observer: PerformanceObserver) {
    observer.observe({ type: "mark", buffered: true });
  }

  private disconnectPerformanceObserver(observer: PerformanceObserver): void {
    setTimeout(() => {
      observer.disconnect();
    }, 60000);
  }

  /**
   * This method us used for processing data that is added to the window.fandomTrack.q
   */
  trackEvent(fandomTrackData: LoadTimeTrackData): void {
    if (!isLoadTimeEvent(fandomTrackData)) {
      return;
    }

    if (this.isEligible(fandomTrackData.event)) {
      this.performanceMarkEvent(fandomTrackData);
      const { event } = fandomTrackData as { event: LoadTimeEvent };
      sendTimeMeter(fandomTrackData, this.getAlias(event));
    }
  }

  /* v8 ignore start */
  performanceMarkEvent({ event, payload }: TrackDataUnion) {
    switch (event) {
      case LoadTimeEvent.AE_INIT:
        performance.mark(`odyssey_${event}`);
        break;
      case LoadTimeEvent.SLOT_REQUESTED:
      case LoadTimeEvent.SLOT_RENDERED:
        performance.mark(`odyssey_${payload.slotName}_${event}`);
        break;
    }
  }
  /* v8 ignore stop */

  /**
   * Used for renaming the event to the apporpiate
   * Note: it would be better to move this config into the `sender` code itself
   * since this does not vary.
   *
   * TODO: move this as a next refactor step
   *
   */
  private getAlias(event: LoadTimeEvent): string | undefined {
    const mapping: Record<LoadTimeEvent, string | undefined> = {
      [LoadTimeEvent.AE_INIT]: undefined,
      [LoadTimeEvent.SLOT_REQUESTED]: "request",
      [LoadTimeEvent.SLOT_RENDERED]: "render",
      [LoadTimeEvent.VENDOR_TIME_UPDATE]: undefined,
    };
    return mapping[event];
  }

  /**
   * Find all the PerformanceMarks we want to track and send them
   * to the tracking endpoint.
   */
  trackPerformanceMarks(entries: PerformanceObserverEntryList) {
    const marks = entries.getEntriesByType("mark") as PerformanceMark[];
    marks.forEach((entry): void => {
      if (entry) {
        const trackingData = this.transformEligiblePerformanceMark(entry);
        if (trackingData) {
          sendPerformanceMark(trackingData);
          this.trackDurations(entry);
        }
      }
    });
  }

  /**
   */
  trackDurations(entry: PerformanceMark) {
    try {
      const markConfig =
        this.eligiblePerformanceMarks[entry.name as PerformanceMarkName];

      if (markConfig && markConfig.duration) {
        const startMark = this.findMatchingStartMark(
          markConfig.duration.start,
          entry,
        );

        if (!startMark) {
          console.warn(
            "Could not measure performance duration: no matching start mark found",
            entry,
          );
          return;
        }
        const measure = performance.measure(markConfig.duration.name, {
          start: startMark.startTime,
          end: entry.startTime,
        });
        sendPerformanceDuration(measure);
      }
    } catch {
      console.warn("Could not measure performance duration");
    }
  }

  /**
   * Finds a performance mark that matches the given name and has the same auctionId as the entry
   * @param startMarkName The name of the performance mark to find
   * @param entry The performance mark entry to match with
   * @returns The matching performance mark or undefined if none exists
   */
  private findMatchingStartMark(
    startMarkName: string,
    entry: PerformanceMark,
  ): PerformanceMark | undefined {
    const startMarks = performance.getEntriesByName(
      startMarkName,
      "mark",
    ) as PerformanceMark[];

    if (!startMarks.length) {
      return undefined;
    }

    // Only auction end needs to be matched to a comparable auction start
    if (entry.name != PerformanceMarkName.AE_AUCTION_END) {
      return startMarks[startMarks.length - 1];
    }

    // Find a mark with matching auctionId
    return startMarks.find(
      (mark) => mark.detail?.auctionId === entry.detail?.auctionId,
    );
  }
  /**
   * This function is executed for every performance mark that is sent in the PerformanceObserver callback.
   *
   * If the mark is not one that we are interested in tracking, it will return undefined.
   * If it is a relevant mark, a tracking object will be returned.
   */
  transformEligiblePerformanceMark(
    entry: PerformanceMark,
  ): FandomPerformanceMarkTrackData | undefined {
    const markConfig =
      this.eligiblePerformanceMarks[entry.name as PerformanceMarkName];

    if (markConfig === undefined) {
      return undefined;
    }

    if (this.abortRefreshMark(markConfig, entry)) {
      return undefined;
    }

    // Prometheus time (duration) needs to be integer
    const time = Math.round(entry.startTime);
    const alias = getPrometheusAlias(entry.name);

    // Some performance marks are generic, other performance marks are tied to specific slotNames
    const slotRequired = markConfig.slotName !== undefined;
    const slotName: string | undefined = slotRequired
      ? entry.detail?.slotId
      : undefined;

    // Abort if a performance mark's detail.slotName does not match a slot-specific markConfig
    if (slotRequired && markConfig.slotName !== slotName) {
      return undefined;
    }

    return {
      event: entry.name as PerformanceMarkName,
      payload: {
        time: time,
        ...(slotRequired && { slotName: slotName }),
      },
      alias: alias,
    };
  }

  abortRefreshMark(
    markConfig: PerformanceMarkConfig | undefined,
    entry: PerformanceMark,
  ): boolean {
    if (markConfig?.onlyFirstRender != true) {
      return false;
    }

    const renderCycle = entry.detail?.renderCycleCount;
    // first render is required, and we don't know which render it is
    if (!renderCycle) {
      console.warn(`${entry.name} is missing a renderCycleCount`, entry);
      return true;
    }

    if (renderCycle > 1) {
      return true;
    }

    return false;
  }
}
